import React, { useState } from "react";
import userService from "../../api/services/userService";
import toast from "react-hot-toast";
import Loading from "../../components/Loading";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUserCheck,
  FaUserTimes,
  FaImage,
  FaPercentage,
} from "react-icons/fa";

const MatchUsers = () => {
  const [senderEmail, setSenderEmail] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [senderUser, setSenderUser] = useState(null);
  const [receiverUser, setReceiverUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingSender, setFetchingSender] = useState(false);
  const [fetchingReceiver, setFetchingReceiver] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch sender user by email
  const handleFetchSender = async () => {
    if (!senderEmail.trim()) {
      toast.error("Please enter sender email");
      return;
    }

    setFetchingSender(true);
    setSenderUser(null);
    setResult(null);

    try {
      const response = await userService.fetchUserByEmail(senderEmail.trim());
      if (response.user) {
        setSenderUser(response.user);
        toast.success("Sender user fetched successfully!");
      } else {
        toast.error("Sender user not found");
      }
    } catch (error) {
      console.error("Error fetching sender:", error);
      toast.error(
        error?.message || "Failed to fetch sender user. Please try again."
      );
    } finally {
      setFetchingSender(false);
    }
  };

  // Fetch receiver user by email
  const handleFetchReceiver = async () => {
    if (!receiverEmail.trim()) {
      toast.error("Please enter receiver email");
      return;
    }

    setFetchingReceiver(true);
    setReceiverUser(null);
    setResult(null);

    try {
      const response = await userService.fetchUserByEmail(receiverEmail.trim());
      if (response.user) {
        setReceiverUser(response.user);
        toast.success("Receiver user fetched successfully!");
      } else {
        toast.error("Receiver user not found");
      }
    } catch (error) {
      console.error("Error fetching receiver:", error);
      toast.error(
        error?.message || "Failed to fetch receiver user. Please try again."
      );
    } finally {
      setFetchingReceiver(false);
    }
  };

  // Open confirmation modal
  const handleOpenConfirmModal = () => {
    if (!senderUser || !receiverUser) {
      toast.error("Please fetch both sender and receiver users first");
      return;
    }

    if (senderUser.id === receiverUser.id) {
      toast.error("Sender and receiver cannot be the same user");
      return;
    }

    setShowConfirmModal(true);
  };

  // Send notification
  const handleSendNotification = async () => {
    setShowConfirmModal(false);
    setSending(true);
    setResult(null);

    try {
      const response = await userService.sendNotificationToFilteredUser(
        senderUser.id,
        receiverUser.id
      );

      setResult({
        success: true,
        message: response.message || "Notification sent successfully!",
        data: response.data,
      });
      toast.success("Notification sent successfully!");
    } catch (error) {
      console.error("Error sending notification:", error);
      setResult({
        success: false,
        message: error?.message || "Failed to send notification",
      });
      toast.error(error?.message || "Failed to send notification");
    } finally {
      setSending(false);
      setSenderEmail("");
      setReceiverEmail("");
      setSenderUser(null);
      setReceiverUser(null);
    }
  };

  // Reset form
  const handleReset = () => {
    setSenderEmail("");
    setReceiverEmail("");
    setSenderUser(null);
    setReceiverUser(null);
    setResult(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // User Info Card Component
  const UserInfoCard = ({ user, title, type, compact = false }) => {
    if (!user) return null;

    const getStatusBadge = (status, label) => {
      return (
        <span
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
            status ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-600"
          }`}>
          {status ? (
            <FaCheckCircle className="text-xs" />
          ) : (
            <FaTimesCircle className="text-xs" />
          )}
          {label}
        </span>
      );
    };

    if (compact) {
      return (
        <div className="bg-white rounded-lg sm:rounded-xl border-2 border-red-200 shadow-md p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            {user.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.firstName}
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-red-300 flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white flex items-center justify-center text-[#FF2D38] text-base sm:text-xl font-bold border-2 border-red-500 flex-shrink-0">
                {user.firstName?.[0]?.toUpperCase() || "?"}
                {user.lastName?.[0]?.toUpperCase() || ""}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="text-base sm:text-lg font-bold text-gray-800 truncate">
                {user.firstName || ""} {user.lastName || ""}
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {user.email}
              </p>
              {user.age && (
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {user.age} years old • {user.occupation || "N/A"}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-3 sm:p-4">
          <h3 className="text-base sm:text-lg md:text-xl font-bold flex items-center gap-2">
            {type === "sender" ? (
              <FaUser className="text-white text-sm sm:text-base" />
            ) : (
              <FaEnvelope className="text-white text-sm sm:text-base" />
            )}{" "}
            <span className="text-sm sm:text-base md:text-lg">{title}</span>
          </h3>
        </div>

        <div className="p-4 sm:p-5 md:p-6">
          {/* User Avatar & Basic Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6 pb-4 sm:pb-5 md:pb-6 border-b border-gray-200">
            <div className="flex-shrink-0">
              {user.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={user.firstName}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 sm:border-4 border-red-300 shadow-lg"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white flex items-center justify-center text-red-500 text-2xl sm:text-3xl font-bold shadow-lg border-2 sm:border-4 border-red-500">
                  {user.firstName?.[0]?.toUpperCase() || "?"}
                  {user.lastName?.[0]?.toUpperCase() || ""}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h4 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 break-words">
                {user.firstName || ""} {user.lastName || ""}
              </h4>
              <p className="text-xs sm:text-sm text-gray-500 break-all">
                {user.email}
              </p>
              {user.tagline && (
                <p className="text-xs sm:text-sm text-red-600 font-medium mt-1 italic break-words">
                  "{user.tagline}"
                </p>
              )}
              {user.percentage && (
                <div className="mt-2">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <FaPercentage className="text-red-500 text-xs sm:text-sm" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">
                      Profile Completion: {user.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="h-2 rounded-full bg-red-500"
                      style={{ width: `${user.percentage}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-5 md:mb-6 justify-center sm:justify-start">
            {getStatusBadge(user.isActive === 1, "Active")}
            {getStatusBadge(user.isProfileComplete === 1, "Profile Complete")}
            {getStatusBadge(user.isVerified === 1, "Verified")}
            {getStatusBadge(user.isBlur === 0, "Profile Visible")}
            {getStatusBadge(user.isDisable === 0, "Not Disabled")}
            {user.isAdmin === 1 && (
              <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                <FaUserCheck className="text-xs" /> Admin
              </span>
            )}
          </div>

          {/* User Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
            {/* Personal Information */}
            <div className="space-y-2 sm:space-y-3">
              <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                Personal Information
              </h5>
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <FaIdCard className="text-[#FF2D38] w-4" />
                  <span className="text-gray-600 font-medium">User ID:</span>
                  <span className="text-gray-800 font-semibold">
                    #{user.id}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaEnvelope className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Email:</span>
                  </div>
                  <span className="text-gray-800 break-all">{user.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaPhone className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Phone:</span>
                  </div>
                  <span className="text-gray-800">
                    {user.contactNo || "N/A"}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaUser className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium">Gender:</span>
                  </div>
                  <span className="text-gray-800">{user.gender || "N/A"}</span>
                </div>
                {user.age && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Age:</span>
                    <span className="text-gray-800">{user.age} years</span>
                  </div>
                )}
                {user.height && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Height:</span>
                    <span className="text-gray-800">{user.height} cm</span>
                  </div>
                )}
                {user.maritalStatus && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Marital Status:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.maritalStatus}
                    </span>
                  </div>
                )}
                {user.religion && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Religion:</span>
                    <span className="text-gray-800 break-words">
                      {user.religion}
                    </span>
                  </div>
                )}
                {user.sect && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Sect:</span>
                    <span className="text-gray-800 break-words">
                      {user.sect}
                    </span>
                  </div>
                )}
                {user.diet && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Diet:</span>
                    <span className="text-gray-800 break-words">
                      {user.diet}
                    </span>
                  </div>
                )}
                {user.smoking && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Smoking:</span>
                    <span className="text-gray-800 break-words">
                      {user.smoking}
                    </span>
                  </div>
                )}
                {user.alcohol && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Alcohol:</span>
                    <span className="text-gray-800 break-words">
                      {user.alcohol}
                    </span>
                  </div>
                )}
                {user.languages && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Languages:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.languages}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Professional & Location Information */}
            <div className="space-y-2 sm:space-y-3">
              <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                Professional & Location
              </h5>
              <div className="space-y-1.5 sm:space-y-2">
                {user.occupation && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Occupation:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.occupation}
                    </span>
                  </div>
                )}
                {user.education && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Education:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.education}
                    </span>
                  </div>
                )}
                {user.careerStatus && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Career Status:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.careerStatus}
                    </span>
                  </div>
                )}
                {user.monthlyIncome && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Monthly Income:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.monthlyIncome}
                    </span>
                  </div>
                )}
                {user.address && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                      <span className="text-gray-600 font-medium">
                        Address:
                      </span>
                    </div>
                    <span className="text-gray-800 break-words">
                      {user.address}
                    </span>
                  </div>
                )}
                {user.grewUp && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Grew Up:</span>
                    <span className="text-gray-800 break-words">
                      {user.grewUp}
                    </span>
                  </div>
                )}
                {user.residential && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Residential:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.residential}
                    </span>
                  </div>
                )}
                {user.marriage && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Marriage Plan:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.marriage}
                    </span>
                  </div>
                )}
                {user.parenting && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">
                      Parenting Goal:
                    </span>
                    <span className="text-gray-800 break-words">
                      {user.parenting}
                    </span>
                  </div>
                )}
                {user.childerns !== undefined && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600 font-medium">Children:</span>
                    <span className="text-gray-800">
                      {user.childerns === 0
                        ? "No children"
                        : `${user.childerns} children`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* About Me & Hobbies */}
          {(user.aboutMe || user.hobbiesNames) && (
            <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
              {user.aboutMe && (
                <div className="mb-3 sm:mb-4">
                  <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                    About Me
                  </h5>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed bg-gray-50 p-2 sm:p-3 rounded-lg border border-red-100 break-words">
                    {user.aboutMe}
                  </p>
                </div>
              )}
              {user.hobbiesNames && (
                <div>
                  <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2">
                    Hobbies & Interests
                  </h5>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {user.hobbiesNames.split(",").map((hobby, idx) => (
                      <span
                        key={idx}
                        className="px-2 sm:px-3 py-0.5 sm:py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200">
                        {hobby.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Account Status */}
          <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
            <h5 className="text-xs sm:text-sm font-bold text-red-600 uppercase tracking-wide mb-2 sm:mb-3">
              Account Details
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              {user.createdDate && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Created:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs sm:text-sm">
                    {formatDate(user.createdDate)}
                  </span>
                </div>
              )}
              {user.modifiedDate && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaCalendarAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Modified:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs sm:text-sm">
                    {formatDate(user.modifiedDate)}
                  </span>
                </div>
              )}
              {user.latitude && user.longitude && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <FaMapMarkerAlt className="text-[#FF2D38] w-3 sm:w-4 flex-shrink-0" />
                    <span className="text-gray-600 font-medium text-xs sm:text-sm">
                      Location:
                    </span>
                  </div>
                  <span className="text-gray-800 text-xs break-all">
                    {user.latitude}, {user.longitude}
                  </span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-600 font-medium text-xs sm:text-sm">
                  Login Type:
                </span>
                <div className="flex flex-wrap gap-1">
                  {user.isGoogle === 1 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      Google
                    </span>
                  )}
                  {user.isApple === 1 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                      Apple
                    </span>
                  )}
                  {user.isGoogle === 0 && user.isApple === 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                      Email
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          {(user.daily_proposals_used !== undefined ||
            user.last_proposal_reset ||
            user.isFace ||
            user.isHidden !== undefined) && (
            <div className="mt-4 sm:mt-5 md:mt-6 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
              <h5 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 sm:mb-3">
                Additional Information
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                {user.daily_proposals_used !== undefined && (
                  <div>
                    <span className="text-gray-600">Daily Proposals Used:</span>
                    <span className="ml-2 font-semibold text-gray-800">
                      {user.daily_proposals_used}
                    </span>
                  </div>
                )}
                {user.last_proposal_reset && (
                  <div>
                    <span className="text-gray-600">Last Reset:</span>
                    <span className="ml-2 text-gray-800">
                      {formatDate(user.last_proposal_reset)}
                    </span>
                  </div>
                )}
                {user.isFace !== undefined && (
                  <div>
                    <span className="text-gray-600">Face Verification:</span>
                    {getStatusBadge(user.isFace === 1, "")}
                  </div>
                )}
                {user.isHidden !== undefined && (
                  <div>
                    <span className="text-gray-600">Hidden:</span>
                    {getStatusBadge(user.isHidden === 0, "Visible")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl w-full h-full min-h-[calc(100vh-120px)] shadow-xl p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-red-600 mb-2 px-2">
            Send Notification Between Users
          </h1>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg px-2">
            Fetch users by email and send matched profile notifications
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-5 md:mb-6">
          {/* Sender Section */}
          <div className="bg-white p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 text-gray-800 flex items-center gap-2">
              <span className="bg-red-500 text-white p-1.5 sm:p-2 rounded-lg border-2 border-red-500">
                <FaUser className="text-white text-sm sm:text-base" />
              </span>
              <span className="text-base sm:text-lg md:text-xl">
                Sender User
              </span>
            </h2>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-gray-700">
                  Sender Email Address
                </label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <input
                    type="email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="e.g., sender@example.com"
                    className="flex-1 p-2.5 sm:p-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    disabled={fetchingSender}
                  />
                  <button
                    onClick={handleFetchSender}
                    disabled={fetchingSender || !senderEmail.trim()}
                    className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all ${
                      fetchingSender || !senderEmail.trim()
                        ? "opacity-50 cursor-not-allowed transform-none"
                        : ""
                    }`}>
                    {fetchingSender ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="hidden sm:inline">Fetching...</span>
                        <span className="sm:hidden">...</span>
                      </span>
                    ) : (
                      "Fetch User"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Receiver Section */}
          <div className="bg-white p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 text-gray-800 flex items-center gap-2">
              <span className="bg-red-500 text-white p-1.5 sm:p-2 rounded-lg border-2 border-red-500">
                <FaEnvelope className="text-white text-sm sm:text-base" />
              </span>
              <span className="text-base sm:text-lg md:text-xl">
                Receiver User
              </span>
            </h2>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold mb-2 text-gray-700">
                  Receiver Email Address
                </label>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <input
                    type="email"
                    value={receiverEmail}
                    onChange={(e) => setReceiverEmail(e.target.value)}
                    placeholder="e.g., receiver@example.com"
                    className="flex-1 p-2.5 sm:p-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                    disabled={fetchingReceiver}
                  />
                  <button
                    onClick={handleFetchReceiver}
                    disabled={fetchingReceiver || !receiverEmail.trim()}
                    className={`w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all ${
                      fetchingReceiver || !receiverEmail.trim()
                        ? "opacity-50 cursor-not-allowed transform-none"
                        : ""
                    }`}>
                    {fetchingReceiver ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="hidden sm:inline">Fetching...</span>
                        <span className="sm:hidden">...</span>
                      </span>
                    ) : (
                      "Fetch User"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Cards Display */}
        {(senderUser || receiverUser) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-5 md:mb-6">
            {senderUser && (
              <UserInfoCard
                user={senderUser}
                title="Sender User"
                type="sender"
              />
            )}
            {receiverUser && (
              <UserInfoCard
                user={receiverUser}
                title="Receiver User"
                type="receiver"
              />
            )}
          </div>
        )}

        {/* Send Notification Button */}
        {senderUser && receiverUser && (
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-4 sm:pt-5 md:pt-6 px-2">
            <button
              onClick={handleOpenConfirmModal}
              disabled={
                sending ||
                !senderUser ||
                !receiverUser ||
                senderUser.id === receiverUser.id
              }
              className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all ${
                sending ||
                !senderUser ||
                !receiverUser ||
                senderUser.id === receiverUser.id
                  ? "opacity-50 cursor-not-allowed transform-none"
                  : ""
              }`}>
              <span className="flex items-center justify-center gap-2">
                <FaEnvelope />{" "}
                <span className="text-sm sm:text-base">Send Notification</span>
              </span>
            </button>
            <button
              onClick={handleReset}
              disabled={sending || fetchingSender || fetchingReceiver}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gray-500 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-gray-600 transition shadow-lg text-sm sm:text-base">
              Reset
            </button>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && senderUser && receiverUser && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-4">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 sm:p-5 md:p-6 rounded-t-xl sm:rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
                    <FaEnvelope className="text-base sm:text-lg md:text-xl" />
                    <span className="text-sm sm:text-base md:text-lg">
                      Confirm Notification
                    </span>
                  </h2>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="text-white hover:text-gray-200 transition p-1">
                    <FaTimesCircle className="text-xl sm:text-2xl" />
                  </button>
                </div>
                <p className="mt-2 text-xs sm:text-sm text-red-100">
                  Review the details before sending the notification
                </p>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-5 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-5 md:mb-6">
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-red-600 mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                      Sender User
                    </h3>
                    <UserInfoCard
                      user={senderUser}
                      title=""
                      type="sender"
                      compact={true}
                    />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-red-600 mb-2 sm:mb-3 border-b-2 border-red-200 pb-1.5 sm:pb-2">
                      Receiver User
                    </h3>
                    <UserInfoCard
                      user={receiverUser}
                      title=""
                      type="receiver"
                      compact={true}
                    />
                  </div>
                </div>

                <div className="bg-red-50 border-2 border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-5 md:mb-6">
                  <h4 className="font-bold text-sm sm:text-base text-red-800 mb-2">
                    Notification Details:
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 break-words">
                    <strong>Title:</strong>{" "}
                    {`Congrats! ${senderUser.firstName || ""} ${
                      senderUser.lastName || ""
                    } found you compatible! Let's chat!`}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-700 mt-1 break-words">
                    <strong>Message:</strong>{" "}
                    {`It seems like ${senderUser.firstName || ""} ${
                      senderUser.lastName || ""
                    } found you compatible! Let's chat!`}
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 p-4 sm:p-5 md:p-6 rounded-b-xl sm:rounded-b-2xl flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 border-t-2 border-red-200">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base bg-gray-500 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-gray-600 transition">
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  disabled={sending}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all ${
                    sending
                      ? "opacity-50 cursor-not-allowed transform-none"
                      : ""
                  }`}>
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FaCheckCircle /> Confirm & Send
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result Display */}
        {result && (
          <div className="mt-4 sm:mt-5 md:mt-6 px-2">
            <div
              className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border-2 shadow-lg ${
                result.success
                  ? "bg-gradient-to-br from-green-50 to-green-100 border-green-400"
                  : "bg-gradient-to-br from-red-50 to-red-100 border-red-400"
              }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                {result.success ? (
                  <FaCheckCircle className="text-2xl sm:text-3xl text-green-600 flex-shrink-0" />
                ) : (
                  <FaTimesCircle className="text-2xl sm:text-3xl text-red-600 flex-shrink-0" />
                )}
                <h3
                  className={`font-bold text-base sm:text-lg md:text-xl ${
                    result.success ? "text-green-700" : "text-red-700"
                  }`}>
                  {result.success
                    ? "Notification Sent Successfully!"
                    : "Error Sending Notification"}
                </h3>
              </div>
              <p
                className={`mb-3 sm:mb-4 text-sm sm:text-base md:text-lg ${
                  result.success ? "text-green-800" : "text-red-800"
                }`}>
                {result.message}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchUsers;
